import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState, useCallback } from 'react';
import { kebabCase } from 'lodash';
import PropTypes from 'prop-types';
import Checkbox from 'react-three-state-checkbox';
import { Modal, ModalBody, ModalHeader, ModalFooter, Button } from 'reactstrap';
import * as ramda from 'ramda';
import entityComposition from '../common/classes/entityComposition';
const createWarning = query => {
  const extra = 'Employees associated with the selection you made must be present in all the jobs associated with your selection.  Try filtering by job before assigning.';
  if (!query || !query.filters || !query.filters.job) {
    return "No employees associated with the selection you made.  ".concat(extra);
  }
  const jobs = ramda.uniq(query.filters.job.map(job => job.jobNumber));
  const last = jobs.pop();
  return jobs.length === 0 ? "No employees found for your selection with job number ".concat(last, ".  ").concat(extra) : "No employees found that are assigned to jobs with numbers ".concat(jobs.join(', '), " and ").concat(last, ".  ").concat(extra);
};
const allSelectedForEmployee = (employeeID, assignments, selectedIDs, entity) => {
  const removeNonEmployeeRecords = list => list.filter(item => item.userID === employeeID);
  const extractAssignedIDs = list => list.map(a => a["".concat(entity, "ID")]);
  const sortIDs = ramda.sort((l, x) => l.localeCompare(x));
  const assignmentIDs = ramda.pipe(removeNonEmployeeRecords, extractAssignedIDs, ramda.uniq, sortIDs)(assignments);
  const sortedIDs = sortIDs(selectedIDs);
  return ramda.equals(assignmentIDs, sortedIDs);
};
const AssignmentModal = _ref => {
  let _ref$employeeList = _ref.employeeList,
    employeeList = _ref$employeeList === void 0 ? [] : _ref$employeeList,
    _ref$employeesSelecte = _ref.employeesSelected,
    employeesSelected = _ref$employeesSelecte === void 0 ? [] : _ref$employeesSelecte,
    _ref$entity = _ref.entity,
    entity = _ref$entity === void 0 ? 'report' : _ref$entity,
    assignments = _ref.assignments,
    isOpen = _ref.isOpen,
    closeModal = _ref.closeModal,
    employeeModalAssignIndexQuery = _ref.employeeModalAssignIndexQuery,
    _ref$employeeModalAss = _ref.employeeModalAssignActions,
    indexCancel = _ref$employeeModalAss.indexCancel,
    select = _ref$employeeModalAss.select,
    selectClear = _ref$employeeModalAss.selectClear,
    selectClearAll = _ref$employeeModalAss.selectClearAll,
    selected = _ref.selected,
    saveAssignments = _ref.saveAssignments;
  const _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    removals = _useState2[0],
    setRemovals = _useState2[1];

  // functions to call when the modal closes
  const cleanUpActions = useCallback(() => {
    setRemovals([]); // clear removal state
    selectClearAll(); // clear selections
    indexCancel(); // clear employee list
  }, [indexCancel, selectClearAll, setRemovals]);
  const handleSelect = useCallback((_ref2, isSelected) => {
    let id = _ref2.id;
    const filteredRemovals = removals.filter(removeID => removeID !== id);
    // The following logic helps us determine whether we've reset to the original state or not
    // const assignments = selected.map(selection => extractAssignedFromContent(selection.content));
    const selectDeselect = assignments.length !== 0 && !assignments.some(_ref3 => {
      let userID = _ref3.userID;
      return userID === id;
    });
    const allSelected = allSelectedForEmployee(id, assignments, selected, entity);
    // If a selection was made _and_ the selection wasn't made in the original list
    if (isSelected && !allSelected) {
      select(id);
      // A selection wasn't made and the checkbox wasn't selected and then deselected before saving
    } else if (!isSelected && !selectDeselect) {
      selectClear(id);
      filteredRemovals.push(id);
    } else if (!isSelected) {
      selectClear(id);
    }
    setRemovals(filteredRemovals);
  }, [assignments, entity, removals, select, selectClear, selected]);
  const handleClose = useCallback(() => {
    cleanUpActions();
    closeModal();
  }, [cleanUpActions, closeModal]);
  const handleSave = useCallback(() => {
    saveAssignments(employeesSelected, removals);
    cleanUpActions();
  }, [employeesSelected, removals, saveAssignments, cleanUpActions]);
  const noEmployeesMessage = createWarning(employeeModalAssignIndexQuery);
  return /*#__PURE__*/React.createElement(Modal, {
    isOpen: isOpen
  }, /*#__PURE__*/React.createElement(ModalHeader, null, employeeList.length > 0 ? 'Select employees to assign' : 'No employees available'), /*#__PURE__*/React.createElement(ModalBody, null, employeeList.length > 0 ? employeeList.map(employee => {
    const id = employee.id,
      firstName = employee.firstName,
      lastName = employee.lastName;
    const displayName = "".concat(firstName, " ").concat(lastName);
    const onSelect = event => handleSelect(employee, event.target.checked);
    const allReports = allSelectedForEmployee(id, assignments, selected, entity);
    const atLeastOne = assignments.some(_ref4 => {
      let userID = _ref4.userID;
      return userID === id;
    });
    const listSelected = employeesSelected.some(employeeID => employeeID === id);
    const removalSelected = removals.some(removalID => removalID === id);
    const checked = (allReports || listSelected) && !removalSelected;
    const indeterminate = !allReports && atLeastOne && !listSelected && !removalSelected;
    return /*#__PURE__*/React.createElement("div", {
      key: id
    }, /*#__PURE__*/React.createElement(Checkbox, {
      className: "t-am-".concat(kebabCase(displayName), " three-state-checkbox"),
      type: "checkbox",
      onChange: onSelect,
      checked: checked,
      indeterminate: indeterminate
    }), displayName);
  }) : noEmployeesMessage), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Button, {
    onClick: handleClose,
    className: "t-am-cancel"
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    disabled: !employeesSelected.length && !removals.length,
    className: "t-am-assign",
    onClick: handleSave
  }, "Assign")));
};
AssignmentModal.propTypes = {
  assignments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  closeModal: PropTypes.func.isRequired,
  saveAssignments: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  employeeList: PropTypes.arrayOf(PropTypes.shape({})),
  employeesSelected: PropTypes.arrayOf(PropTypes.string),
  employeeModalAssignIndexQuery: PropTypes.shape({}).isRequired,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  employeeModalAssignActions: PropTypes.shape({
    select: PropTypes.func,
    selectClear: PropTypes.func,
    selectClearAll: PropTypes.func,
    serverFilter: PropTypes.func,
    indexCancel: PropTypes.func
  }).isRequired,
  entity: PropTypes.string
};
export { AssignmentModal as TestAssignmentModal };
export default entityComposition(AssignmentModal, [], {
  employeeList: state => state.employeeModalAssign.list,
  employeesSelected: state => state.employeeModalAssign.selected,
  employeeModalAssignIndexQuery: state => state.employeeModalAssign.indexQuery,
  loading: state => state.employeeModalAssign.loading
}, ['employeeModalAssign']);